/* React */
import React, { useEffect, useRef } from "react";

/* React Redux */
import {
  useDispatch,
  useSelector,
  useStore,
} from "react-redux";
import { AppDispatch, RootState } from "src/redux/store";

/* React Router */
import { useHistory, useLocation } from "react-router";

/* Ionic */
import { IonButton, IonIcon } from "@ionic/react";
import { close } from "ionicons/icons";

/* Thunks */
import { requestAgentSpec } from "src/redux/thunks/agent-chat/agentSpecThunk";
import {
  applySpecToControls,
  undoAppliedSpec,
} from "src/redux/thunks/agent-chat/agentSpecActions";

/* Utils */
import LanguageSelect from "src/components/ai-assistant/language-select/LanguageSelect";
import ModelSelect from "src/components/ai-assistant/model-select/ModelSelect";
import { resetSpecConversation } from "src/redux/features/agent-chat/agentChatSlice";
import { fetchAllowedAgentModels } from "src/redux/thunks/agent-chat/agentChatThunk";
import { AGENT_LOCAL_MODEL } from "src/shared/interfaces/api/AgentSpec";
import { buildAgentCurrentState } from "src/utils/helper/agent-spec/buildCurrentState";
import {
  AGENT_ANALYSIS_PATHS,
  analysisFromPath,
} from "src/utils/helper/agent-spec/specRoutes";

/* Components */
import ChatInput from "src/components/ai-assistant/chat-input/ChatInput";
import PrimaryButton from "src/components/shared/buttons/primary-button/PrimaryButton";

/* Stylesheet */
import styles from "./AssistantPanel.module.scss";

interface AssistantPanelProps {
  onClose: () => void;
}

/**
 * Chat panel that configures the insights controls.
 *
 * The agent answers a prompt with a configuration spec, never with
 * data: this panel applies the spec to the controls and lets the
 * page's existing fetch hook load the figures from the insights
 * backend. What changed is spelled out, and a single Undo puts the
 * controls back.
 */
const AssistantPanel: React.FC<AssistantPanelProps> = ({
  onClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const history = useHistory();
  const { pathname } = useLocation();

  const {
    specStatus,
    specError,
    lastSummary,
    clarification,
    unresolved,
    undoSnapshot,
    selectedModel,
    defaultModel,
    modelsStatus,
    answerLanguage,
    specHistory,
  } = useSelector((state: RootState) => state.agentChat);

  const isLoading = specStatus === "loading";

  /* What this prompt will be read by: the picked model, or the one the
     deployment falls back to when none is sent. */
  const effectiveModel = selectedModel ?? defaultModel;

  /**
   * Closing the panel unmounts it, but an in-flight request keeps
   * running: without this the controls would change and the page would
   * navigate seconds after the user dismissed the assistant.
   *
   * Only one request can be in flight at a time — the input is
   * disabled while loading — so a single mounted flag is enough.
   */
  const isMounted = useRef(true);
  useEffect(
    () => () => {
      isMounted.current = false;
      pending.current?.abort();
    },
    []
  );

  /**
   * Undo has to return the user to the page they were on as well as
   * restore the controls — a spec that changes the analysis navigates,
   * and restoring only the controls would leave them somewhere they
   * never chose to be.
   */
  const handleUndo = () => {
    const previousPath = dispatch(undoAppliedSpec());
    if (previousPath && previousPath !== pathname) {
      history.push(previousPath);
    }
  };

  /**
   * The allowed models, for the picker. Fetched here because the panel is
   * now the only way into the assistant — the standalone page that used to
   * do it is not routed while /chat is a stub.
   */
  useEffect(() => {
    if (modelsStatus === "idle") {
      dispatch(fetchAllowedAgentModels());
    }
  }, [dispatch, modelsStatus]);

  /**
   * Holds the running request so it can be abandoned. A spec applied after
   * the user gave up on it would move the controls out from under them.
   */
  const pending = useRef<{ abort: () => void } | null>(null);

  const handleCancel = () => {
    pending.current?.abort();
    pending.current = null;
  };

  const handleSend = async (prompt: string) => {
    const currentState = buildAgentCurrentState(
      store.getState(),
      analysisFromPath(pathname)
    );

    const request = dispatch(
      requestAgentSpec({
        prompt,
        model: selectedModel,
        answerLanguage,
        currentState,
        history: specHistory,
      })
    );
    pending.current = request;
    const result = await request;
    pending.current = null;

    /**
     * No guard is needed for a cancel that lands as the request resolves,
     * though it was raised in review. An abort before resolution rejects
     * the thunk, so `fulfilled.match` below is false; an abort after it
     * cannot interleave, because this continuation is a microtask and a
     * click is not. Add another `await` above this line and that stops
     * being true.
     */

    /**
     * `pathname` is the route the prompt was submitted from. The panel
     * lives in the toolbar and so survives navigation: without this
     * check a late response would apply a spec computed for the page
     * the user has already left, overwriting whatever they did since.
     */
    if (
      !isMounted.current ||
      history.location.pathname !== pathname ||
      !requestAgentSpec.fulfilled.match(result) ||
      !result.payload.spec
    ) {
      return;
    }

    const { spec } = result.payload;
    dispatch(applySpecToControls(spec, pathname));

    /**
     * Navigate only when the spec asks for a different analysis;
     * the page's own fetch hook then reacts to the new controls.
     */
    const path = AGENT_ANALYSIS_PATHS[spec.analysis];
    if (!pathname.startsWith(path)) {
      history.push(path);
    }
  };

  return (
    <div
      className={styles.assistant_panel}
      data-testid="assistant-panel"
    >
      <div className={styles.header}>
        <span className={styles.title}>AI Assistant</span>
        {/* Beside the title rather than by the input: these set how
            the assistant replies, not what this one prompt says. */}
        <ModelSelect />
        <LanguageSelect />
        <IonButton
          fill="clear"
          data-testid="assistant-panel-close"
          aria-label="Close the AI Assistant"
          onClick={onClose}
        >
          <IonIcon icon={close} />
        </IonButton>
      </div>

      {/* Says what this does before the user has spent a prompt
          finding out. A placeholder alone does not: it is gone the
          moment they start typing, and "how many patients are
          there" comes back as a spec that changes nothing, which
          reads as the assistant having ignored them. */}
      {!lastSummary && !clarification && !specError ? (
        <p
          className={styles.hint}
          data-testid="assistant-hint"
        >
          I set the dashboard up for you — I don&apos;t answer with
          numbers. Try &quot;survival by sex since 2018&quot; or
          &quot;age distribution&quot;.
        </p>
      ) : undefined}

      {lastSummary ? (
        <p
          className={styles.summary}
          data-testid="assistant-summary"
        >
          {lastSummary}
        </p>
      ) : undefined}

      {unresolved.length > 0 ? (
        <ul
          className={styles.unresolved}
          data-testid="assistant-unresolved"
        >
          {unresolved.map((field) => (
            <li key={`${field.field}-${field.requested}`}>
              {`${field.requested}: ${field.reason}`}
            </li>
          ))}
        </ul>
      ) : undefined}

      {clarification ? (
        <p
          className={styles.clarification}
          data-testid="assistant-clarification"
        >
          {/* Marked because this text is the model's own words, unlike
              the summary above it, which this frontend composes from the
              spec that passed validation. A reader cannot otherwise tell
              which of the two they are looking at. */}
          <span
            className={styles.ai_badge}
            data-testid="assistant-ai-badge"
          >
            AI-generated
          </span>{" "}
          {clarification}
        </p>
      ) : undefined}

      {specError ? (
        <p
          className={styles.error}
          data-testid="assistant-error"
        >
          {specError}
        </p>
      ) : undefined}

      {isLoading ? (
        <div className={styles.loading}>
          <p data-testid="assistant-loading">Working on it…</p>
          <IonButton
            fill="clear"
            size="small"
            data-testid="assistant-cancel"
            aria-label="Stop waiting for the AI Assistant"
            onClick={handleCancel}
          >
            Cancel
          </IonButton>
        </div>
      ) : undefined}

      {undoSnapshot ? (
        <PrimaryButton
          testId="assistant-undo"
          type="button"
          ariaLabel="Undo the applied changes"
          label="Undo"
          onClick={handleUndo}
        />
      ) : undefined}

      {/* A question changed nothing, so there is nothing to undo — but it
          leaves the exchange open, and the next prompt would be read as an
          answer to it. This is the way out for a user who would rather ask
          something else. */}
      {clarification ? (
        <PrimaryButton
          testId="assistant-reset"
          type="button"
          ariaLabel="Discard the question and start over"
          label="Start over"
          /* Not while a reply is in flight: clearing the exchange
             underneath a running request would drop the history the
             answer is about to be added to. */
          disabled={isLoading}
          onClick={() => dispatch(resetSpecConversation())}
        />
      ) : undefined}

      <ChatInput
        disabled={isLoading}
        onSend={handleSend}
        placeholder="Ask for an analysis to show"
      />

      {/* Where the words the user types actually go. Model-dependent
          because the answer differs: a provider model is a third party
          outside this deployment, and `test` is a keyword matcher inside
          it that reaches no network at all. Saying "sent to a provider"
          in both cases would be wrong half the time. */}
      {effectiveModel ? (
        <p
          className={styles.disclosure}
          data-testid="assistant-disclosure"
        >
          {effectiveModel === AGENT_LOCAL_MODEL
            ? "Your prompt is interpreted inside this deployment and is not sent to a model provider."
            : `Your prompt and the available filter options are sent to ${effectiveModel} to be interpreted. No patient data is sent.`}
        </p>
      ) : undefined}
    </div>
  );
};

export default AssistantPanel;
